<template>
  <BottomSheetModal
    v-model="open"
    hide-confirm
    :hide-header="true"
    @close="onClose"
  >
    <div class="bus">
      <button class="bus-cancel" @click="onClose">{{ t('common.cancel') }}</button>
      <h2 class="bus-title">{{ t('block.title') }}</h2>
      <p class="bus-intro">{{ t('block.subtitle') }}</p>

      <div v-if="target" class="bus-target">
        <div class="bus-target-kind">{{ t('block.aboutToBlock') }}</div>
        <div class="bus-target-row">
          <div class="bus-target-img bus-target-img--round">
            <img v-if="target.image" :src="target.image" :alt="target.name" />
            <span v-else class="bus-target-fallback">{{ target.initials || '?' }}</span>
          </div>
          <div class="bus-target-info">
            <div class="bus-target-name">{{ target.name }}</div>
            <div class="bus-target-type">{{ t('report.targets.user') }}</div>
          </div>
        </div>
      </div>

      <div class="bus-info">
        <v-icon size="20" class="bus-info-icon">mdi-information-outline</v-icon>
        <p class="bus-info-text">{{ t('block.description') }}</p>
      </div>

      <p v-if="serverError" class="bus-error">{{ serverError }}</p>
      <p v-if="success" class="bus-success">{{ t('block.successText') }}</p>

      <button
        type="button"
        class="bus-submit"
        :disabled="loading || success"
        @click="submit"
      >
        <v-progress-circular v-if="loading" size="18" width="2" indeterminate color="white" class="mr-2" />
        <v-icon v-else-if="success" size="20" class="mr-2">mdi-check-circle</v-icon>
        {{ success ? t('block.successBtn') : (loading ? t('common.sending') : t('block.confirm')) }}
      </button>
    </div>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import axiosInstance from '@/services/axiosInstance'

interface BlockTarget {
  id: string
  name: string
  image?: string
  initials?: string
}

const props = defineProps<{
  modelValue: boolean
  target: BlockTarget | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'blocked', userId: string): void
}>()

const { t } = useI18n()
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'

const open = ref(props.modelValue)
const loading = ref(false)
const success = ref(false)
const serverError = ref('')

watch(() => props.modelValue, (v) => {
  open.value = v
  if (v) {
    loading.value = false
    success.value = false
    serverError.value = ''
  }
})
watch(open, (v) => emit('update:modelValue', v))

function onClose() {
  open.value = false
}

async function submit() {
  if (!props.target?.id) return
  loading.value = true
  serverError.value = ''
  try {
    await axiosInstance.post(`${API_BASE_URL}/users/${props.target.id}/block`)
    success.value = true
    emit('blocked', props.target.id)
    setTimeout(() => {
      open.value = false
    }, 900)
  } catch (err: any) {
    console.error('[BlockUserModal] block failed:', err)
    serverError.value = err?.response?.data?.message || t('block.failed')
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.bus {
  padding: 4px 0 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: rgb(var(--v-theme-sheetText));
}

.bus-cancel {
  align-self: flex-start;
  background: transparent;
  border: 0;
  padding: 4px 0;
  color: rgb(var(--v-theme-primary));
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
}

.bus-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: rgb(var(--v-theme-sheetText));
}

.bus-intro {
  font-size: 14px;
  line-height: 1.45;
  color: rgba(var(--v-theme-sheetText), 0.7);
  margin: 0;
}

.bus-target {
  background: rgba(var(--v-theme-sheetText), 0.05);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bus-target-kind {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(var(--v-theme-sheetText), 0.55);
  font-weight: 600;
}

.bus-target-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bus-target-img {
  width: 44px;
  height: 44px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 8px;

  &--round { border-radius: 50%; }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.bus-target-fallback {
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

.bus-target-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.bus-target-name {
  font-size: 15px;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bus-target-type {
  font-size: 12px;
  color: rgba(var(--v-theme-sheetText), 0.6);
}

.bus-info {
  background: rgba(217, 119, 6, 0.12);
  border: 1px solid rgba(217, 119, 6, 0.35);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.bus-info-icon {
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 2px;
}

.bus-info-text {
  font-size: 14px;
  line-height: 1.45;
  color: rgba(var(--v-theme-sheetText), 0.85);
  margin: 0;
}

.bus-error {
  color: #ef4444;
  font-size: 13px;
  margin: 0;
}

.bus-success {
  color: #22c55e;
  font-size: 13px;
  margin: 0;
}

.bus-submit {
  margin-top: 4px;
  background: #dc2626;
  color: #fff;
  border: 0;
  border-radius: 12px;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
</style>
